import React from 'react';
import type { DictionaryCatalogItem, DictionaryItem } from '@/services/bff/types/dictionary';

type DictionaryListener = () => void;

let dictionaryCatalog: DictionaryCatalogItem[] = [];
let dictionaryVersion = 0;
const listeners = new Set<DictionaryListener>();

const emitChange = () => {
  dictionaryVersion += 1;
  listeners.forEach(listener => {
    listener();
  });
};

export const setDictionaryCatalog = (nextCatalog: DictionaryCatalogItem[]) => {
  dictionaryCatalog = Array.isArray(nextCatalog) ? nextCatalog : [];
  emitChange();
};

export const getDictionaryCatalog = () => dictionaryCatalog;

export const subscribeDictionaryCatalog = (listener: DictionaryListener) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const useDictionaryVersion = () =>
  React.useSyncExternalStore(
    subscribeDictionaryCatalog,
    () => dictionaryVersion,
    () => dictionaryVersion,
  );

export const getDictionaryEntry = (dictionaryCode: string) =>
  dictionaryCatalog.find(item => item.code === dictionaryCode);

export const getDictionaryItems = (dictionaryCode: string) => {
  const entry = getDictionaryEntry(dictionaryCode);
  return entry?.items?.filter(item => item.isEnabled) || [];
};

export const getDictionaryOptions = (dictionaryCode: string) =>
  getDictionaryItems(dictionaryCode).map(item => ({
    label: item.label,
    value: item.code,
  }));

export const getDictionaryLabel = (
  dictionaryCode: string,
  itemCode?: string | null,
  fallback = itemCode || '-',
) => {
  if (!itemCode) {
    return fallback;
  }

  const item = getDictionaryItems(dictionaryCode).find(option => option.code === itemCode);
  return item?.label || fallback;
};

export const getDictionaryItem = (
  dictionaryCode: string,
  itemCode?: string | null,
) => {
  if (!itemCode) {
    return undefined;
  }
  return getDictionaryItems(dictionaryCode).find(item => item.code === itemCode);
};

export const updateDictionaryItemInCatalog = (dictionaryCode: string, item: DictionaryItem) => {
  const nextCatalog = dictionaryCatalog.map(entry => {
    if (entry.code !== dictionaryCode) {
      return entry;
    }
    const nextItems = entry.items.some(current => current.uuid === item.uuid)
      ? entry.items.map(current => (current.uuid === item.uuid ? item : current))
      : [...entry.items, item];
    return {
      ...entry,
      items: nextItems.sort((left, right) => left.sortOrder - right.sortOrder),
    };
  });

  dictionaryCatalog = nextCatalog;
  emitChange();
};

export const setDictionaryItemsInCatalog = (dictionaryCode: string, items: DictionaryItem[]) => {
  dictionaryCatalog = dictionaryCatalog.map(entry => {
    if (entry.code !== dictionaryCode) {
      return entry;
    }
    return {
      ...entry,
      items: [...items].sort((left, right) => left.sortOrder - right.sortOrder),
    };
  });
  emitChange();
};

export const removeDictionaryItemFromCatalog = (dictionaryCode: string, itemUuid: string) => {
  dictionaryCatalog = dictionaryCatalog.map(entry => {
    if (entry.code !== dictionaryCode) {
      return entry;
    }
    return {
      ...entry,
      items: entry.items.filter(item => item.uuid !== itemUuid),
    };
  });
  emitChange();
};
